import { useState } from 'react'

import { Minus, Plus } from 'lucide-react'

const ProgramPhase = () => {
  const [activeAccordion, setActiveAccordion] = useState<number | null>(null)

  const accordionItems = [
    {
      title: 'N5 Phase (6 weeks)',
      content: [
        '800 vocab, 100 Kanji',
        'Hiragana & Katakana, Basic Grammar',
        'JLPT N5 Tryout & Speaking Exam',
      ],
    },
    {
      title: 'N4 Phase (6 weeks)',
      content: [
        '1.500 vocab, 300 Kanji',
        'Pre-Intermediate Grammar',
        'JLPT N4 Tryout & Speaking Exam',
      ],
    },
    {
      title: 'N3 Phase (14 weeks)',
      content: [
        '3.000 vocab, 700 Kanji',
        'Honorific Language, Workplace Conversations',
        'JLPT N3 Tryout & Speaking Exam',
      ],
    },
  ]

  return (
    <section className="bg-red-600 py-10">
      <div className="max-w-8xl mx-auto px-6">
        <div className="mb-8 text-center">
          <h2 className="text-3xl font-bold text-white">Our Program Phase</h2>
        </div>

        {/* Desktop Version */}
        <DesktopVersion />

        {/* Mobile Version */}
        <MobileVersion />

        <div className="mx-auto mt-3 max-w-5xl">
          <div className="space-y-4">
            {accordionItems.map((item, index) => (
              <div key={index} className="rounded-lg bg-red-400 text-white">
                <button
                  className="flex w-full items-center justify-between p-6 text-left"
                  onClick={() =>
                    setActiveAccordion(activeAccordion === index ? null : index)
                  }
                >
                  <h4 className="text-lg font-semibold">{item.title}</h4>
                  {activeAccordion === index ? (
                    <Minus className="h-6 w-6" />
                  ) : (
                    <Plus className="h-6 w-6" />
                  )}
                </button>
                {activeAccordion === index && (
                  <div className="px-6 pb-6">
                    <ul className="space-y-2">
                      {item.content.map((contentItem, contentIndex) => (
                        <li key={contentIndex} className="text-sm">
                          {contentItem}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

const DesktopVersion = () => {
  return (
    <>
      <div className="hidden md:block">
        <div className="relative rounded-2xl bg-white p-6">
          {/* Phase Cards */}
          <div className="grid gap-8 md:grid-cols-2">
            {/* N3 Ready Bootcamp */}
            <div className="h-[550px] rounded-lg border border-gray-200 p-4">
              <div className="relative mb-5 h-[156px] w-[90%] rounded-lg bg-gradient-to-r from-red-600 to-red-800 p-5">
                <div className="mb-2 text-2xl font-bold text-white">
                  N3 Ready
                  <br />
                  Bootcamp
                  <div className="absolute ml-5 inline-block rounded-2xl border border-white px-3 py-1 text-white">
                    <span className="flex gap-2 text-xs font-bold">
                      <img src="/img/gijinkoku/uilcalender.svg" alt="icon" />
                      6,5 months
                    </span>
                  </div>
                </div>
                <div className="mb-5 text-base text-white">
                  Mulai dari dasar (N5) hingga pre-advanced (N3) <br />
                  dengan metode hybrid learning
                </div>
                <img
                  src="/img/gijinkoku/Group1797.svg"
                  alt="icon"
                  className="absolute"
                  style={{ top: '-11px', right: '-60px' }}
                  width={145}
                />
              </div>

              <div className="space-y-4">
                {[
                  'Live Sessions bersama Sensei berpengalaman (Senin - Kamis)',
                  'Speaking Practice dengan Speaking Assistant',
                  'Self-Learning di YES LMS: latihan Kanji, Vocab, Reading, Listening',
                  'Weekly Knowledge Check setiap Jumat',
                ].map((item, index) => (
                  <div key={index} className="flex gap-5">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-600 text-sm font-bold text-white">
                      {(index + 1).toString().padStart(2, '0')}
                    </div>
                    <div className="flex-1 text-xl leading-tight text-gray-700">
                      <span className="font-bold">
                        {item.split(' ')[0]} {item.split(' ')[1]}
                      </span>
                      <br />
                      <span>{item.split(' ').slice(2).join(' ')}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Work Preparation */}
            <div className="h-[550px] rounded-lg border border-gray-200 p-4">
              <div className="relative mb-5 w-[93%] rounded-lg bg-gradient-to-r from-red-600 to-red-800 p-5">
                <div className="mb-2 text-2xl font-bold text-white">
                  Work
                  <br />
                  Preparation
                  <div className="absolute ml-5 inline-block rounded-2xl border border-white px-3 py-1 text-white">
                    <span className="flex gap-2 text-xs font-bold">
                      <img src="/img/gijinkoku/uilcalender.svg" alt="icon" />1
                      month
                    </span>
                  </div>
                </div>
                <div className="mb-5 text-base text-white">
                  Persiapan kerja di Jepang
                </div>
                <img
                  src="/img/gijinkoku/Group-13.png"
                  alt="icon"
                  className="absolute"
                  style={{ right: '-47px', top: '0' }}
                  width="145"
                />
              </div>

              <div className="space-y-4">
                {[
                  '2x Career Mentoring Live Sessions (CV Writing)',
                  '2x1-on-1 Coaching Sessions (Mock Interview)',
                  'Fleksibel, sesuai dengan jadwal kamu dan Career Coach',
                ].map((item, index) => (
                  <div key={index} className="flex gap-5">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-600 text-sm font-bold text-white">
                      {(index + 1).toString().padStart(2, '0')}
                    </div>
                    <div className="flex-1 text-xl leading-tight text-gray-700">
                      <span className="font-bold">
                        {item.split(' ')[0]} {item.split(' ')[1]}
                      </span>
                      <br />
                      <span>{item.split(' ').slice(2).join(' ')}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {/* Program Timeline */}
          <div className="mb-8">
            <div className="mt-4 flex h-16 w-full items-center justify-between rounded-full bg-gray-200 p-0">
              <div className="w-[50%] px-8 text-center text-lg font-bold text-gray-700">
                N3 Ready Bootcamp (6,5 months)
              </div>
              <div className="ml-4 flex h-16 w-[50%] items-center justify-center rounded-full bg-red-600 px-8">
                <div className="text-lg font-bold text-white">
                  Work Preparation (1 month)
                </div>
              </div>
            </div>
          </div>
          {/* Timeline phases */}
          <div className="relative mx-auto flex max-w-5xl items-center justify-center gap-2">
            {/* N5 Phase */}
            <div className="relative z-10 text-center">
              <div className="relative">
                {/* Trapezoid shape using CSS clip-path */}
                <div
                  className="relative mb-2 flex h-[120px] w-[186px] flex-col items-center justify-center"
                  style={{
                    backgroundImage: `url(/img/gijinkoku/vector-2403.png)`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  <span className="text-2xl font-bold text-gray-700">N5</span>
                  <span className="text-2xl font-bold text-gray-700">
                    Levels
                  </span>
                  <div className="mt-1 text-xs text-gray-600">6 Weeks</div>
                </div>
              </div>
            </div>

            {/* N4 Phase */}
            <div className="relative z-10 text-center">
              <div className="relative">
                {/* Trapezoid shape using CSS clip-path */}
                <div
                  className="relative mb-2 flex h-[120px] w-[186px] flex-col items-center justify-center shadow-sm"
                  style={{
                    backgroundImage: `url(/img/gijinkoku/vector-2403.png)`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  <span className="text-2xl font-bold text-gray-700">N4</span>
                  <span className="text-2xl font-bold text-gray-700">
                    Levels
                  </span>
                  <div className="mt-1 text-xs text-gray-600">6 Weeks</div>
                </div>
              </div>
            </div>

            {/* N3 Phase */}
            <div className="relative z-10 text-center">
              <div className="relative">
                {/* Trapezoid shape using CSS clip-path */}
                <div
                  className="relative mb-2 flex h-[120px] w-[186px] flex-col items-center justify-center shadow-sm"
                  style={{
                    backgroundImage: `url(/img/gijinkoku/vector-2403.png)`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  <span className="text-2xl font-bold text-gray-700">N3</span>
                  <span className="text-2xl font-bold text-gray-700">
                    Levels
                  </span>
                  <div className="mt-1 text-xs text-gray-600">14 Weeks</div>
                </div>
              </div>
            </div>

            {/* Work Preparation Phase */}
            <div className="relative z-10 text-center">
              <div className="relative">
                {/* Trapezoid shape using CSS clip-path */}
                <div
                  className="relative mb-2 flex h-[120px] w-[567px] flex-col items-center justify-center shadow-sm"
                  style={{
                    backgroundImage: `url(/img/gijinkoku/Group-1855.png)`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                >
                  <span className="text-2xl font-bold text-white">
                    Work Preparation
                  </span>
                  <span className="text-2xl font-bold text-white">6 Weeks</span>
                </div>
              </div>
            </div>
            <div className="absolute top-1/2 h-0.5 w-10 w-full translate-y-[-50%] border-t-2 border-dashed border-red-500"></div>
          </div>
        </div>
      </div>
    </>
  )
}

const MobileVersion = () => {
  return (
    <div className="block md:hidden">
      <div className="relative rounded-2xl bg-white p-4">
        {/* N3 Ready Bootcamp - Mobile Version */}
        <div className="mb-6 rounded-lg border border-gray-200 p-4">
          <div className="relative mb-5 h-auto w-full rounded-lg bg-gradient-to-r from-red-600 to-red-800 p-5 md:h-[156px]">
            <div className="mb-2 text-2xl font-bold text-white">
              N3 Ready
              <br />
              Bootcamp
              <div className="ml-2 inline-block rounded-2xl border border-white px-3 py-1 text-white sm:absolute">
                <span className="flex gap-2 text-xs font-bold">
                  <img src="/img/gijinkoku/uilcalender.svg" alt="icon" />
                  6,5 months
                </span>
              </div>
            </div>
            <div className="mb-5 text-base text-white">
              Mulai dari dasar (N5) hingga pre-advanced (N3) <br />
              dengan metode hybrid learning
            </div>
          </div>

          <div className="mb-6 space-y-4">
            {[
              'Live Sessions bersama Sensei berpengalaman (Senin - Kamis)',
              'Speaking Practice dengan Speaking Assistant',
              'Self-Learning di YES LMS: latihan Kanji, Vocab, Reading, Listening',
              'Weekly Knowledge Check setiap Jumat',
            ].map((item, index) => (
              <div key={index} className="flex gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-600 text-sm font-bold text-white">
                  {(index + 1).toString().padStart(2, '0')}
                </div>
                <div className="flex-1 text-base leading-tight text-gray-700">
                  <span className="font-bold">
                    {item.split(' ')[0]} {item.split(' ')[1]}
                  </span>
                  <br />
                  <span>{item.split(' ').slice(2).join(' ')}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Mobile Timeline for N3 Ready Bootcamp */}
        <div className="mb-4 rounded-full bg-gray-200 px-4 py-3">
          <div className="text-center text-sm font-bold text-gray-700">
            N3 Ready Bootcamp (6,5 months)
          </div>
        </div>

        {/* Mobile Phase Cards */}
        <div className="relative mx-auto mb-2 mb-4 flex justify-between gap-2">
          <div
            className="z-10 flex-1 rounded-lg p-3 text-center"
            style={{
              backgroundImage: `url(/img/gijinkoku/vector-2403.png)`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            <div className="text-sm font-bold text-gray-700">N5</div>
            <div className="text-sm font-bold text-gray-700">Levels</div>
            <div className="mt-1 text-xs text-gray-600">6 Weeks</div>
          </div>

          <div
            className="z-10 flex-1 rounded-lg p-3 text-center"
            style={{
              backgroundImage: `url(/img/gijinkoku/vector-2403.png)`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            <div className="text-sm font-bold text-gray-700">N4</div>
            <div className="text-sm font-bold text-gray-700">Levels</div>
            <div className="mt-1 text-xs text-gray-600">6 Weeks</div>
          </div>

          <div
            className="z-10 flex-1 rounded-lg p-3 text-center"
            style={{
              backgroundImage: `url(/img/gijinkoku/vector-2403.png)`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          >
            <div className="text-sm font-bold text-gray-700">N3</div>
            <div className="text-sm font-bold text-gray-700">Levels</div>
            <div className="mt-1 text-xs text-gray-600">14 Weeks</div>
          </div>

          <div className="absolute left-[10%] top-1/2 h-0.5 w-10 w-[80%] translate-y-[-50%] border-t-2 border-dashed border-red-500"></div>
        </div>

        {/* Work Preparation - Mobile Version */}
        <div className="mb-4 rounded-lg border border-gray-200 p-4">
          <div className="relative mb-5 w-full rounded-lg bg-gradient-to-r from-red-600 to-red-800 p-5">
            <div className="mb-2 text-2xl font-bold text-white">
              Work
              <br />
              Preparation
              <div className="absolute ml-5 inline-block rounded-2xl border border-white px-3 py-1 text-white">
                <span className="flex gap-2 text-xs font-bold">
                  <img src="/img/gijinkoku/uilcalender.svg" alt="icon" />1 month
                </span>
              </div>
            </div>
            <div className="mb-5 text-base text-white">
              Persiapan kerja di Jepang
            </div>
          </div>

          <div className="mb-6 space-y-4">
            {[
              '2x Career Mentoring Live Sessions (CV Writing)',
              '2x1-on-1 Coaching Sessions (Mock Interview)',
              'Fleksibel, sesuai dengan jadwal kamu dan Career Coach',
            ].map((item, index) => (
              <div key={index} className="flex gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-600 text-sm font-bold text-white">
                  {(index + 1).toString().padStart(2, '0')}
                </div>
                <div className="flex-1 text-base leading-tight text-gray-700">
                  <span className="font-bold">
                    {item.split(' ')[0]} {item.split(' ')[1]}
                  </span>
                  <br />
                  <span>{item.split(' ').slice(2).join(' ')}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        {/* Mobile Timeline for Work Preparation */}
        <div className="mb-4 rounded-full bg-red-600 px-4 py-3">
          <div className="text-center text-sm font-bold text-white">
            Work Preparation (1 month)
          </div>
        </div>

        {/* Work Preparation Phase Card */}
        <div className="rounded-lg bg-red-600 p-4 text-center">
          <div className="text-lg font-bold text-white">Work Preparation</div>
          <div className="text-sm text-white">6 Weeks</div>
        </div>
      </div>
    </div>
  )
}

export default ProgramPhase
